import {
  Typography,
  styled,
  Grid,
  Table,
  TableRow,
  TableCell,
  Button,
  Dialog,
  Box,
  IconButton,
  ButtonBase,
  TooltipProps,
  Tooltip,
  tooltipClasses,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { BackButton } from '../../../../../components/UI/Button';

export const CustomBackButton = styled(BackButton)(() => ({
  margin: '-20px 0 -10px -30px',
}));

export const GridSection = styled(Grid)(({ theme }) => ({
  background: '#FFFFFE',
  boxShadow: '0px 3px 6px #00000029',
  borderRadius: 20,
  padding: '40px',
  [theme.breakpoints.down('xxl')]: {
    padding: '32px',
  },
}));

export const StatusAlert = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  borderRadius: '12px',
  padding: '16px',
  marginTop: '24px',
  '& .material-symbols-outlined': {
    color: theme.palette.primary.main,
    fontSize: '20px',
  },
}));

export const StatusAlertIcon = styled('span')(() => ({
  display: 'inline-flex',
}));

export const StatusAlertText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.common.grey,
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: '600',
  color: theme.palette.common.grey,
}));

export const StyledInput = styled(Typography)(() => ({
  fontWeight: '500',
  fontSize: '16px',
  marginRight: 8,
}));

export const GridItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  gap: 8,
  marginTop: '28px',
  [theme.breakpoints.down('xl')]: {
    marginTop: '20px',
  },
}));

export const StyledTable = styled(Table)(() => ({
  tableLayout: 'fixed',
  width: '100%',
  minWidth: '900px',
}));

export const TableCellHeader = styled(TableCell)(({ theme }) => ({
  color: theme.palette.secondary.main,
  font: '600 21px Montserrat',
  backgroundColor: '#F1F5FA',
  verticalAlign: 'middle',
  padding: '20px 16px',
  '& span': {
    verticalAlign: 'bottom',
    paddingLeft: '4px',
  },
  [theme.breakpoints.down('xl')]: {
    fontSize: '16px',
    '& span': {
      fontSize: '18px',
    },
  },
}));

export const StyledCell = styled(TableCell)(({ theme }) => ({
  color: '#707070',
  padding: '20px 12px',
  fontSize: '16px',
  [theme.breakpoints.down('xl')]: {
    padding: '18px 8px',
  },
}));

export const StatusCell = styled(TableCell)(({ theme }) => ({
  padding: '20px 12px',
  fontSize: '16px',
  fontWeight: '600',
  [theme.breakpoints.down('xl')]: {
    padding: '18px 8px',
  },
}));

export const ButtonsWrapper = styled(Box)(() => ({
  display: 'flex',
  gap: '20px',
  justifyContent: 'center',
}));

export const StyledRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
    backgroundColor: '#F1F5FA66',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const OutlinedButton = styled(Button)(({ theme }) => ({
  maxWidth: '120px',
  height: '40px',
  border: `1.5px solid ${theme.palette.secondary.main}`,
  color: theme.palette.secondary.main,
  backgroundColor: '#FAFCFF',
  textTransform: 'lowercase',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
  '&:disabled': {
    color: theme.palette.secondary.main,
  },
}));

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    minWidth: '500px',
    maxWidth: '800px',
    maxHeight: '600px',
    borderRadius: 16,
    backgroundColor: '#FAFCFF',
  },
  '& .MuiDialog-paperScrollPaper': {
    '&::-webkit-scrollbar': {
      width: 10,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: 10,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
    '&::-webkit-scrollbar-track': {
      margin: '8px 0',
      borderRadius: '16px',
    },
  },
}));

export const DialogContainer = styled(Box)(() => ({
  minWidth: 700,
  minHeight: 200,
  padding: '40px 40px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
}));

export const TextTypography = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignContent: 'center',
  span: {
    color: theme.palette.secondary.main,
  },
}));

// Hierarquia dos diálogos de confirmação: título com peso, corpo sem — e o
// conteúdo centralizado, como no ConfirmDialog dos comunicados.
export const DialogTitleTypography = styled(Typography)(() => ({
  fontSize: 18,
  fontWeight: 600,
  color: '#1A1A1A',
  textAlign: 'center',
}));

export const DialogTextTypography = styled(Typography)(() => ({
  fontSize: 15,
  color: '#475467',
  lineHeight: '150%',
  textAlign: 'center',
}));

// Ações do diálogo no padrão justificado: um botão em cada borda.
export const DialogActionsBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
}));

export const DownloadButton = styled(IconButton)(({ theme }) => ({
  padding: 0,
  span: {
    color: theme.palette.primary.main,
  },
  '&:disabled': {
    span: {
      filter: 'opacity(45%)',
    },
  },
}));

export const ActionButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.primary.main,
  textAlign: 'justify',
  fontSize: '16px',
  display: 'block',
  width: '100%',
  minWidth: 0,
  span: {
    display: 'block',
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '&:disabled': {
    span: {
      filter: 'opacity(45%)',
    },
  },
}));

export const DocumentNameButton = styled(ActionButton)(({ theme }) => ({
  [theme.breakpoints.down('xl')]: {
    fontSize: '14px',
  },
}));

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    textAlign: 'justify',
    fontSize: '12px',
    padding: '8px',
    fontWeight: 'normal',
    border: '1px solid #dadde9',
    boxShadow: '0px 3px 6px #00000029',
    marginTop: '8px !important',
  },
}));

export const StatusChip = styled(ButtonBase)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px',
  borderRadius: 20,
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  '& .material-symbols-outlined': {
    fontSize: 18,
  },
  [theme.breakpoints.down('xl')]: {
    fontSize: '13px',
  },
}));

export const StatusChipChevron = styled('span')(() => ({
  display: 'inline-flex',
  '& .material-symbols-outlined': {
    fontSize: 13,
  },
}));

export const HistoryList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const HistoryItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: 12,
}));

export const HistoryIndicator = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const HistoryModalHeader = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  padding: '20px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flexShrink: 0,
}));

export const HistoryModalHeaderTop = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

export const HistoryModalBody = styled(Box)(() => ({
  padding: '20px 32px 40px 24px',
  minHeight: 100,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
}));

export const HistoryIconBadge = styled(Box)(() => ({
  width: 26,
  height: 40,
  borderRadius: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& .material-symbols-outlined': {
    fontSize: 22,
  },
}));

export const HistoryLine = styled(Box)(() => ({
  flex: 1,
  width: 2,
  minHeight: 16,
  backgroundColor: '#E0E0E0',
  margin: '4px 0',
}));

export const HistoryContent = styled(Box)(() => ({
  paddingTop: 8,
  flex: 1,
}));

export const HistoryHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

export const HistoryAuthor = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#8C8F96',
  margin: '8px 8px 0 0',
}));

export const HistoryEntryText = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.common.grey,
}));

export const HistoryMeta = styled(Typography)(() => ({
  fontSize: '14px',
  color: '#8C8F96',
  whiteSpace: 'nowrap',
}));

export const HistoryReason = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.common.grey,
  margin: '8px 8px 8px 0',
}));

export const HistoryFileButton = styled(ButtonBase)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  margin: '8px 8px 0 0',
  maxWidth: '100%',
  padding: '8px 12px',
  fontSize: '14px',
  color: theme.palette.common.grey,
  backgroundColor: theme.palette.common.white,
  border: '1px solid #E0E0E0',
  borderRadius: '10px',
  gap: '8px',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
  },
  // minWidth releases the flex item so the ellipsis can actually kick in.
  '& > span:first-of-type': {
    minWidth: 0,
    maxWidth: '300px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .material-symbols-outlined': {
    flexShrink: 0,
  },
}));

export const CloseButtonBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row-reverse',
}));

export const CloseIcon = styled(IconButton)(({ theme }) => ({
  margin: '20px 20px -50px 0',
  '& span': {
    fontSize: '24px',
    fontWeight: '600',
    color: theme.palette.secondary.main,
  },
}));
